import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import {
  Field,
  HTMLWrapper,
  Textarea,
  VisuallyHidden
} from "@/storybook-components";
import type { ChangeEvent } from "react";
import type { Question } from "@/types";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

interface EssayQuestionProps {
  question: Question;
}

export const validate = async (
  answer: string,
  question: Question
): Promise<string[]> => {
  let schema = string();
  if (question.required) {
    schema = schema.required();
  }
  try {
    await schema.validate(answer);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const EssayQuestion = ({ question }: EssayQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const answer = answers[question.id] || "";

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e.target.value
      })
    );
  };

  return (
    <Field.Root
      my={4}
      id={`${question.id}`}
      invalid={error.length > 0}
      required={question.required}
      disabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <Field.Label>
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </Field.Label>
      <Textarea
        value={answer}
        onChange={handleChange}
        size={{ base: "lg", lg: "md" }}
        data-testid="essayQuestion-input"
        name="essayQuestion-input"
        autoComplete="off"
      />
      {error.length > 0 && (
        <Field.ErrorText data-testid="essayQuestion-errorMessage">
          <Field.ErrorIcon />
          {translate(`errors.${error}`)}
        </Field.ErrorText>
      )}
      <Field.HelperText>{question.notes}</Field.HelperText>
    </Field.Root>
  );
};

export default EssayQuestion;
